// src/components/PackagePreview.jsx
import React from 'react'
import { motion } from 'framer-motion'
import { Link } from 'react-router-dom'
import { 
  Star, 
  Clock, 
  TrendingUp, 
  Shield, 
  Zap,
  ArrowRight,
  Check,
  Crown
} from 'lucide-react'

const PackagePreview = ({ pkg, index = 0 }) => {
  const getPackageColor = (level) => {
    switch (level) {
      case 'PREMIUM': return 'from-purple-500 to-pink-500'
      case 'ADVANCED': return 'from-blue-500 to-cyan-500'
      case 'BEGINNER': return 'from-green-500 to-emerald-500'
      default: return 'from-gray-500 to-gray-600'
    }
  }

  const getPackageIcon = (level) => {
    switch (level) {
      case 'PREMIUM': return Crown
      case 'ADVANCED': return TrendingUp
      case 'BEGINNER': return Star
      default: return Zap
    }
  }

  const getLevelBadge = (level) => {
    const colors = {
      PREMIUM: 'bg-gradient-to-r from-purple-500 to-pink-500',
      ADVANCED: 'bg-gradient-to-r from-blue-500 to-cyan-500',
      BEGINNER: 'bg-gradient-to-r from-green-500 to-emerald-500'
    }
    
    const labels = {
      PREMIUM: 'Premium',
      ADVANCED: 'Advanced', 
      BEGINNER: 'Beginner'
    }

    return (
      <span className={`${colors[level]} text-white px-3 py-1 rounded-full text-xs font-bold`}>
        {labels[level]}
      </span>
    )
  }

  const Icon = getPackageIcon(pkg.level)

  return (
    <motion.div
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ delay: index * 0.1 }}
      className="group"
    >
      <div className="bg-white dark:bg-gray-800 rounded-3xl shadow-xl hover:shadow-2xl transition-all duration-500 border border-gray-200 dark:border-gray-700 group-hover:scale-105 overflow-hidden">
        {/* Header */}
        <div className={`bg-gradient-to-r ${getPackageColor(pkg.level)} p-6 text-white relative overflow-hidden`}>
          {/* Background Pattern */}
          <div className="absolute inset-0 opacity-10">
            <div className="absolute top-0 right-0 w-32 h-32 bg-white rounded-full -mr-16 -mt-16"></div>
            <div className="absolute bottom-0 left-0 w-24 h-24 bg-white rounded-full -ml-12 -mb-12"></div>
          </div>

          <div className="relative z-10">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center space-x-3">
                <div className="bg-white/20 backdrop-blur-sm rounded-2xl p-3">
                  <Icon className="w-6 h-6" />
                </div>
                <div>
                  <h3 className="text-xl font-bold">{pkg.name}</h3>
                  {getLevelBadge(pkg.level)}
                </div>
              </div>
            </div>

            {/* ROI Badge */}
            <div className="bg-white/20 backdrop-blur-sm rounded-2xl p-4 text-center">
              <div className="text-3xl font-bold mb-1">{pkg.dailyROI}%</div>
              <div className="text-white/80 text-sm">Daily ROI</div>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="p-6">
          {/* Investment Range */}
          <div className="grid grid-cols-2 gap-4 mb-6">
            <div className="text-center">
              <div className="text-sm text-gray-600 dark:text-gray-400 mb-1">Min Investment</div>
              <div className="text-lg font-bold text-gray-900 dark:text-white">
                ${pkg.minInvestment?.toLocaleString()}
              </div>
            </div>
            <div className="text-center">
              <div className="text-sm text-gray-600 dark:text-gray-400 mb-1">Max Investment</div>
              <div className="text-lg font-bold text-gray-900 dark:text-white">
                ${pkg.maxInvestment ? pkg.maxInvestment.toLocaleString() : 'Unlimited'}
              </div>
            </div>
          </div>

          {/* Features */}
          <div className="space-y-3 mb-6">
            {[
              { icon: Clock, text: `${pkg.termDays} Days Term` },
              { icon: Shield, text: 'Capital Protection' },
              { icon: Zap, text: 'Instant Withdrawal' },
              { icon: TrendingUp, text: 'Compounding Available' }
            ].map((feature, idx) => (
              <div key={idx} className="flex items-center space-x-3">
                <feature.icon className="w-4 h-4 text-green-500" />
                <span className="text-sm text-gray-600 dark:text-gray-400">{feature.text}</span>
              </div>
            ))}
          </div>

          {/* Additional Benefits */}
          <div className="bg-gray-50 dark:bg-gray-700/50 rounded-2xl p-4 mb-6">
            <h4 className="font-semibold text-gray-900 dark:text-white mb-2 text-sm">Package Benefits</h4>
            <div className="space-y-2">
              {pkg.features?.slice(0, 3).map((feature, idx) => (
                <div key={idx} className="flex items-center space-x-2">
                  <Check className="w-3 h-3 text-green-500" />
                  <span className="text-xs text-gray-600 dark:text-gray-400">{feature}</span>
                </div>
              )) || (
                <>
                  <div className="flex items-center space-x-2">
                    <Check className="w-3 h-3 text-green-500" />
                    <span className="text-xs text-gray-600 dark:text-gray-400">24/7 Support</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Check className="w-3 h-3 text-green-500" />
                    <span className="text-xs text-gray-600 dark:text-gray-400">Risk Management</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Check className="w-3 h-3 text-green-500" />
                    <span className="text-xs text-gray-600 dark:text-gray-400">Auto Reinvest</span>
                  </div>
                </>
              )}
            </div>
          </div>

          {/* CTA Button */}
          <Link
            to="/auth/register"
            className="w-full bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white font-semibold py-4 px-6 rounded-xl transition-all transform hover:scale-105 flex items-center justify-center space-x-2 group/btn"
          >
            <span>Invest Now</span>
            <ArrowRight className="w-4 h-4 group-hover/btn:translate-x-1 transition-transform" />
          </Link>
        </div>

        {/* Hover Effect */}
        <div className="absolute inset-0 bg-gradient-to-r from-blue-600/5 to-purple-600/5 rounded-3xl opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none"></div>
      </div>
    </motion.div>
  )
}

export default PackagePreview